// Идентификаторы источников/слоёв MapLibre плюс глобальные тюнинг-константы,
// не зависящие от состояния компонента - вынесены из MapView.vue как первый
// шаг разбиения файла (см. constants.ts/styleHelpers.ts/poiIcons.ts/
// cliffAerialwayIcons.ts/labels.ts/basemapLayers.ts в этой же папке).
import type { FeatureCollection } from "geojson";

// ── Идентификаторы источников/слоёв ──────────────────────────────────────
// У MapLibre нет LayerGroup: только именованный source плюс один или
// несколько слоёв поверх него. Базовая карта - пять векторных (MVT)
// источников от martin прямо из PostGIS (см. infra:martin/config.yaml и функции
// public.trails_tile/pois_tile/roads_tile в Alembic 20260825_02, а также
// contours_tile/landcover_tile в 20260825_03/04), стилизуется целиком на
// клиенте. Id источника здесь всегда совпадает со строкой, которую
// ST_AsMVT() пишет как имя MVT-слоя - несовпадение `source-layer` с
// `source` в addLayer тихое (слой просто ничего не рисует).
export const TRAILS_SOURCE = "trails";
export const POIS_SOURCE = "pois";
// Обычный GeoJSON-источник, а не векторные тайлы, как остальные здесь -
// встроенная кластеризация MapLibre (cluster: true ниже) работает только на
// GeoJSON, ей нужен весь набор соседних точек сразу, а не заранее нарезанный
// тайл. Наполняется на клиенте из уже загруженных векторных тайлов
// POIS_SOURCE (refreshPoiClusterSource), без нового бэкенд-эндпоинта - тот
// же подход «запросить то, что уже на экране», что и у refreshPoiPinMarkers
// для пина спасслужбы.
export const POIS_CLUSTER_SOURCE = "pois-cluster";
export const POIS_CLUSTER_LAYER = "pois-clusters";
// Admin-двойник POIS_SOURCE, читает pois_tile_admin через отдельный
// nginx-путь за auth_request (infra:nginx/nginx.conf) - включает скрытые POI и
// свойства `hidden`/`recently_changed`/`origin`/`source_id`, которых нет в
// публичном тайле. Добавлен безусловно при инициализации карты (как и
// остальные источники), а не лениво при первом включении режима
// редактирования: ни один слой не ссылается на него, пока editMode
// выключен, а MapLibre не фетчит тайлы источника без видимого слоя, так что
// это ничего не стоит для не-редактора.
export const POIS_ADMIN_SOURCE = "pois_admin";
export const ROADS_SOURCE = "roads";
export const CONTOURS_SOURCE = "contours";
export const LANDCOVER_SOURCE = "landcover";
export const WATERWAYS_SOURCE = "waterways";
export const CLIFFS_SOURCE = "cliffs";
export const BUILDINGS_SOURCE = "buildings";
// Admin-двойник BUILDINGS_SOURCE (та же логика, что у POIS_ADMIN_SOURCE
// выше) - читает buildings_tile_admin, несёт hidden/has_override, включает
// только слой buildings-label-admin.
// слои заливки/контура зданий всегда читают BUILDINGS_SOURCE, режим
// редактирования влияет только на подписи, не на контур. Не входит в
// VECTOR_SOURCES по той же причине, что и POIS_ADMIN_SOURCE - ни один слой
// не ссылается на него до включения режима редактирования.
export const BUILDINGS_ADMIN_SOURCE = "buildings_admin";
export const AERIALWAYS_SOURCE = "aerialways";
export const RIDGES_SOURCE = "ridges";
export const POWER_LINES_SOURCE = "power_lines";
export const BOUNDARIES_SOURCE = "boundaries";
export const PLACES_SOURCE = "places";
export const RAILWAYS_SOURCE = "railways";
export const RAILWAY_STATIONS_SOURCE = "railway_stations";
export const VECTOR_SOURCES = [
  LANDCOVER_SOURCE,
  WATERWAYS_SOURCE,
  TRAILS_SOURCE,
  POIS_SOURCE,
  ROADS_SOURCE,
  CONTOURS_SOURCE,
  CLIFFS_SOURCE,
  BUILDINGS_SOURCE,
  AERIALWAYS_SOURCE,
  RIDGES_SOURCE,
  POWER_LINES_SOURCE,
  BOUNDARIES_SOURCE,
  PLACES_SOURCE,
  RAILWAYS_SOURCE,
  RAILWAY_STATIONS_SOURCE,
] as const;
// Два raster-dem источника на один и тот же URL тайлов - отдельно для
// setTerrain и для hillshade. MapLibre сам советует не делить один источник
// между ними ("Please consider using two separate sources to improve
// rendering quality"). Дублирование бесплатно по сети (URL идентичен, кэш
// nginx/браузера отдаёт второй источник из кэша), лишь небольшой лишний
// in-memory кэш тайлов на четыре квадрата SRTM.
export const TERRAIN_SOURCE = "terrain-dem";
export const HILLSHADE_SOURCE = "hillshade-dem";
export const ACCURACY_SOURCE = "user-accuracy";
export const ROUTE_SOURCES = {
  glow: "route-glow",
  casing: "route-casing",
  main: "route-main",
  arrows: "route-arrows",
} as const;

export const EMPTY_FEATURES: FeatureCollection = { type: "FeatureCollection", features: [] };

// ── Инструмент «Линейка» (useMapRuler.ts) ──
// Линия между вершинами. Длина/набор/потеря отрезка больше не рисуются на
// карте (инлайн-подписи + ховер-хинт убраны) - показываются в меню по
// долгому тапу/правому клику на линии, см. RulerMenuState.legStats.
export const RULER_LINE_SOURCE = "ruler-line";

// ── Редактор маршрутов (useRouteEditor.ts) ──
// Отдельный от линейки source/layer - редактор не переиспользует
// useMapRuler.ts (намеренно, см. план "route editor" §5 - линейка уже
// стабильна и недавно доделана, лишний риск трогать её не нужен).
export const ROUTE_EDITOR_LINE_SOURCE = "route-editor-line";

// Приоритет у env-переопределения, иначе кэшированные тайлы через nginx-прокси
// относительно хоста текущей страницы - тот же билд работает через VPN, LAN
// и localhost (см. ту же логику в api.ts's defaultApiUrl).
export const proxiedTileUrl = (path: string) =>
  typeof window !== "undefined" && window.location?.hostname
    ? `${window.location.protocol}//${window.location.hostname}:8083/${path}`
    : `http://localhost:8083/${path}`;

// Три эндпоинта векторных тайлов идут через тот же nginx tile cache, что и
// terrain-тайлы ниже (/vector-tiles/ в infra:nginx/nginx.conf проксирует на
// martin). VITE_VECTOR_TILE_URL - это *база*, имя слоя и {z}/{x}/{y}
// дописываются здесь, так как три слоя отличаются только этим сегментом.
//
// VITE_TILE_URL (растровая база OpenTopoMap) здесь больше не читается - у
// этой карты растровой подложки больше нет вообще. Остальные три
// MapLibre-карты в приложении (RouteMiniMap.vue, ImportPointsTool.vue,
// превью в RouteStyleSection.vue) всё ещё растровые и строят свой URL сами -
// так что переменная окружения и build-arg остаются нужны, просто не этому
// компоненту.
export const vectorTileBase =
  (import.meta as any).env?.VITE_VECTOR_TILE_URL || proxiedTileUrl("vector-tiles");
export const vectorTileUrl = (layer: string) => `${vectorTileBase}/${layer}/{z}/{x}/{y}`;

// Глифы для всех текстовых слоёв. MapLibre не отрисует ни один символ без
// PBF-диапазона глифов - системный шрифт как фолбэк не подходит для
// латиницы/кириллицы (localIdeographFontFamily покрывает только CJK), так
// что подмножество Noto Sans Regular лежит в frontend/public/fonts/ и
// раздаётся same-origin - карта остаётся полностью self-hosted и укладывается
// в CSP (`default-src 'self'`) без дыры на внешний шрифтовый сервер.
export const glyphsUrl = `${(import.meta as any).env?.BASE_URL || "/"}fonts/{fontstack}/{range}.pbf`.replace(
  /\/{2,}/g,
  "/"
);
export const MAP_FONT = ["Noto Sans Regular"];

// Юго-восток Казахстана: хребты Заилийского/Иле-Алатау и Кунгей Ала-Тоо
// целиком, плюс подходы - Чарынский каньон, Кольсай/Каинды, долина Чилика,
// Тургень, БАО, Кастек, Или на север до ~44.3. Примерно 400 x 280 км.
// OSM-экстракт (KZ+KG) покрывает всю эту область; изолинии генерируются под
// этот же прямоугольник backend/scripts/generate_contours.py (его DEFAULT_BBOX)
// и покрываются dem_tiles (SRTM N41-N44 x E074-E079, см. fetch_dem_tiles.py) -
// все три нужно держать синхронно, иначе на карте появится прямой обрыв
// изолиний/затенения на краю покрытия.
// Порядок пар - [долгота, широта], как у MapLibre (обратный Leaflet).
export const MAP_MAX_BOUNDS: [[number, number], [number, number]] = [
  [74.8, 41.8],
  [79.8, 44.3],
];
// Ровно координаты единственного реального узла ("Kusto Мынжылкы", id 1,
// GET /nodes) - не приблизительная точка "где-то в горах южнее города".
// useNodeMarkers.ts::maybeCenterInitially асинхронно "допрыгивает" картой до
// ближайшего узла уже после того, как геолокация и список узлов подъедут - с
// единственным реальным узлом это всегда тот же самый узел, так что если
// начальный центр карты УЖЕ совпадает с его координатами, jumpTo() не
// производит видимого прыжка (нечего анимировать - позиция не изменилась).
// Обновить, если когда-нибудь появится второй реальный узел ближе к
// какому-то другому месту, откуда обычно открывают карту.
export const MAP_DEFAULT_CENTER: [number, number] = [77.079156, 43.087003];

// Terrain-RGB, генерируется бэкендом на лету из dem_tiles и кэшируется тем же
// nginx tile cache, что и векторные тайлы (см. backend/app/terrain/, infra:nginx/nginx.conf).
export const terrainUrlBase =
  (import.meta as any).env?.VITE_TERRAIN_URL || proxiedTileUrl("terrain-tiles/{z}/{x}/{y}.png");

// Подобрано на глаз по силуэту Заилийского Алатау за Алматы: 1.0 выглядит
// почти плоско на используемых зумах, а сильно выше 1.5 предгорья
// превращаются в шипы. Используется в toggle3D (MapView.vue).
export const TERRAIN_EXAGGERATION = 1.5;
export const TERRAIN_PITCH_DEG = 60;

// Ниже этого зума пины узлов и промежуточные точки маршрута только
// захламляют вид без пользы - форму маршрута и так передают линия и
// бейджи начала/конца.
export const DETAIL_MARKER_MIN_ZOOM = 12;

// Общий порог для symbol-слоёв отдельных POI и кластеров.
export const POI_LOD_MIN_ZOOM = 13;
